import React, { useState, useEffect, useCallback } from "react";
import "../css/others/Table.css";
import { FaEdit } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import AddUserPopup from "../popups/AddUserPopup";
import EditUserPopup from "../popups/EditUserPopup";
import { apiCall } from "../main/ApiCall";
import ToastPopup from "../popups/ToastPopup";
import { IUser, IRole, IAuditorSkill, createToastMessage, ToastMessage } from "../interFaces/interfaces";
import EncryptionUtils from "../main/encryptionUtils";
const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<IUser[]>([]);
  const [roles, setRoles] = useState<IRole[]>([]);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [isPromptVisible, setIsPromptVisible] = useState(true);
  const [isAddUserPopupVisible, setIsAddUserPopupVisible] = useState(false);
  const [isEditUserPopupVisible, setIsEditUserPopupVisible] = useState(false);
  const [auditorSkills, setAuditorSkills] = useState<IAuditorSkill[]>([]);
  const [auditorTypes, setAuditorTypes] = useState<{ value: string; description: string }[]>([]);
  const [newUserData, setNewUserData] = useState({ firstName: "", lastName: "", email: "", roleId: "", isPrimaryAuditor: undefined as boolean | undefined, skills: [] as string[], auditorType: "" as string });
  const [editUserData, setEditUserData] = useState<IUser | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [fields, setFields] = useState<any>({});
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [statusToggleUserId, setStatusToggleUserId] = useState<string | null>(null);
  const [statusEnum, setStatusEnum] = useState<{ value: string; description: string }[]>([]);
  const navigate = useNavigate();
  const clientId = localStorage.getItem("clientId");
  const loggedInUserEmail = localStorage.getItem("email");
  const getUserStatus = (user: IUser): "ACTIVE" | "INACTIVE" => {
    return user.userStatus === "ACTIVE" ? "ACTIVE" : "INACTIVE";
  };
  const isAuditorRole = selectedGroup && roles.find((role) => role.id === selectedGroup)?.name.toLowerCase() === "auditor";
  useEffect(() => {
    const permissions = EncryptionUtils.get<any>("permissions", null);
    if (permissions.tabs && permissions.tabs["userManagement"]) {
      setFields(permissions.tabs["userManagement"].fields);
    } else {
      console.log("No permissions found for 'userManagement' tab");
    }
  }, []);
  useEffect(() => {
    apiCall("get", `/api/roles/all/${clientId}`)
      .then((response) => {
        if (Array.isArray(response)) {
          setRoles(response);
        } else {
          console.error("Error fetching roles:", response);
        }
      })
      .catch((error) => {
        console.error("Error fetching roles:", error);
      });
  }, [clientId]);
  useEffect(() => {
    apiCall("get", "/api/enums/UserStatusEnum")
      .then((response) => {
        if (Array.isArray(response)) {
          setStatusEnum(response);
        }
      })
      .catch((error) => {
        console.error("Error fetching status enum:", error);
      });
  }, []);
  useEffect(() => {
    apiCall("get", "/api/enums/AuditorSkillEnum")
      .then((response) => {
        if (Array.isArray(response)) {
          setAuditorSkills(response);
        }
      })
      .catch((error) => {
        console.error("Error fetching auditor skills:", error);
      });
  }, []);
  useEffect(() => {
    apiCall("get", "/api/enums/AuditTypeEnum")
      .then((response) => {
        if (Array.isArray(response)) {
          setAuditorTypes(response);
        }
      })
      .catch((error) => {
        console.error("Error fetching auditor types:", error);
      });
  }, []);
  const fetchUsers = useCallback(() => {
    if (selectedGroup) {
      setLoading(true);
      apiCall("get", "/api/users/role", null, { clientId, roleId: selectedGroup, page: currentPage - 1, size: itemsPerPage })
        .then((data) => {
          setUsers(data.content || []);
          setTotalPages(data.totalPages || 1);
          setTotalElements(data.totalElements || 0);
          setSelectedUsers([]);
          setIsAllSelected(false);
        })
        .catch((error) => console.error("Error fetching users:", error))
        .finally(() => setLoading(false));
    }
  }, [selectedGroup, clientId, currentPage, itemsPerPage]);
  useEffect(() => {
    const savedPage = sessionStorage.getItem("userManagementCurrentPage");
    const savedItemsPerPage = sessionStorage.getItem("userManagementItemsPerPage");
    const savedGroup = sessionStorage.getItem("userManagementSelectedGroup");
    if (savedPage) setCurrentPage(Number(savedPage));
    if (savedItemsPerPage) setItemsPerPage(Number(savedItemsPerPage));
    if (savedGroup) {
      setSelectedGroup(savedGroup);
      setIsPromptVisible(false);
    }
  }, []);
  useEffect(() => {
    if (selectedGroup) {
      fetchUsers();
      if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(1);
        sessionStorage.setItem("userManagementCurrentPage", "1");
      }
    }
  }, [fetchUsers, selectedGroup, currentPage, itemsPerPage, totalPages]);
  const handleGroupSelection = (group: string) => {
    setSelectedGroup(group);
    setIsPromptVisible(false);
    setCurrentPage(1);
    sessionStorage.setItem("userManagementSelectedGroup", group);
    sessionStorage.setItem("userManagementCurrentPage", "1");
  };
  const handleAddUserClick = () => setIsAddUserPopupVisible(true);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewUserData((prevData) => ({ ...prevData, [name]: value }));
  };
  const handleClosePopup = () => setIsAddUserPopupVisible(false);
  const handleSaveNewUser = () => {
    const { firstName, lastName, email, isPrimaryAuditor, skills, auditorType } = newUserData;
    const isAuditorRole = roles.find((role) => role.id === selectedGroup)?.name.toLowerCase() === "auditor";
    const userData: any = { firstName, lastName, email, roleId: selectedGroup, clientId: clientId };
    if (isAuditorRole) {
      userData.isPrimaryAuditor = isPrimaryAuditor;
      userData.skills = skills || [];
      userData.auditorType = auditorType;
    }
    apiCall("post", `/api/users`, userData)
      .then(() => {
        setMessage(createToastMessage("User added successfully!", "success", 3000));
        setIsAddUserPopupVisible(false);
        setNewUserData({ firstName: "", lastName: "", email: "", roleId: "", isPrimaryAuditor: undefined, skills: [], auditorType: "" });
        fetchUsers();
      })
      .catch((error: any) => {
        const errorMessage = error?.response?.data?.message;
        setMessage(createToastMessage(errorMessage, "error", 3000));
      });
  };
  const handleEditUserClick = (user: IUser) => {
    setEditUserData(user);
    setIsEditUserPopupVisible(true);
  };
  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (editUserData) {
      const { name, value } = e.target;
      setEditUserData((prevData) => ({ ...prevData!, [name]: value }));
    }
  };
  const handleSaveEditedUser = () => {
    if (editUserData) {
      const { id, firstName, lastName, email, skills, auditorType } = editUserData;
      const isAuditorRole = roles.find((role) => role.id === editUserData.roleId)?.name.toLowerCase() === "auditor";
      const userData: any = { firstName, lastName, email };
      if (isAuditorRole) {
        userData.skills = skills || []; userData.auditorType = auditorType;
      }
      apiCall("put", `/api/users`, userData, { userId: id })
        .then(() => {
          setMessage(createToastMessage("User updated successfully!", "success", 3000));
          setIsEditUserPopupVisible(false);
          fetchUsers();
        })
        .catch((error: any) => {
          const errorMessage = error?.response?.data?.message;
          setMessage(createToastMessage(errorMessage, "error", 3000));
        });
    }
  };
  const handleStatusToggle = (userId: string) => {
    setStatusToggleUserId(userId);
  };
  const confirmStatusToggle = () => {
    if (!statusToggleUserId) return;
    const user = users.find((u) => u.id === statusToggleUserId);
    if (!user) return;
    const currentStatus = getUserStatus(user);
    const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    apiCall("delete", "/api/users", [statusToggleUserId], { status: newStatus })
      .then(() => {
        setMessage(createToastMessage(`User status updated to ${newStatus}`, "success", 3000));
        fetchUsers();
      })
      .catch((error: any) => {
        const errorMessage = error?.response?.data?.message;
        setMessage(createToastMessage(errorMessage, "error", 3000));
      })
      .finally(() => {
        setStatusToggleUserId(null);
      });
  };
  const handleBulkDelete = () => {
    const usersToDelete = selectedUsers.filter((userId) => {
      const user = users.find((u) => u.id === userId);
      return user?.email !== loggedInUserEmail;
    });
    if (usersToDelete.length > 0) {
      setSelectedUsers(usersToDelete);
      setShowBulkDeleteConfirm(true);
    } else {
      setMessage(createToastMessage("Cannot delete your own account via bulk delete", "warning", 3000));
    }
  };
  const confirmBulkDelete = () => {
    const finalUsersToDelete = selectedUsers.filter((userId) => {
      const user = users.find((u) => u.id === userId);
      return user?.email !== loggedInUserEmail;
    });
    if (finalUsersToDelete.length === 0) {
      setMessage(createToastMessage("No valid users selected for deletion", "warning", 3000));
      setShowBulkDeleteConfirm(false);
      return;
    }
    apiCall("delete", "/api/users", finalUsersToDelete, { status: "INACTIVE" })
      .then(() => {
        setMessage(createToastMessage(`${finalUsersToDelete.length} users deleted successfully!`, "success", 3000));
        fetchUsers();
        setSelectedUsers([]);
        setIsAllSelected(false);
        setShowBulkDeleteConfirm(false);
      })
      .catch((error: any) => {
        const errorMessage = error?.response?.data?.message;
        setMessage(createToastMessage(errorMessage, "error", 3000));
      });
  };
  const toggleUserSelection = (userId: string) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };
  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedUsers([]);
    } else {
      const allExceptLoggedIn = users
        .filter((user) => user.email !== loggedInUserEmail)
        .map((user) => user.id);
      setSelectedUsers(allExceptLoggedIn);
    }
    setIsAllSelected(!isAllSelected);
  };
  const getSkillDescriptions = (skillValues: string[]): string => {
    if (!skillValues || skillValues.length === 0) return "N/A";
    return skillValues
      .map((skillValue) => {
        const skill = auditorSkills.find((s) => s.value === skillValue);
        return skill ? skill.description : skillValue;
      })
      .join(", ");
  };
  const getAuditorTypeDescription = (auditorTypeValue?: string): string => {
    if (!auditorTypeValue) return "N/A";
    const auditorType = auditorTypes.find((type) => type.value === auditorTypeValue);
    return auditorType ? auditorType.description : auditorTypeValue;
  };
  const renderTable = () => {
    const isAuditorRole = selectedGroup && roles.find((role) => role.id === selectedGroup)?.name.toLowerCase() === "auditor";
    if (loading) {
      return (
        <tr>
          <td colSpan={9} className="loading">Loading... </td>
        </tr>
      );
    }
    if (users.length === 0) {
      return (
        <tr>
          <td colSpan={9} className="no-data"> No data available</td>
        </tr>
      );
    }
    return users.map((user) => {
      const baseRoleName = roles.find((role) => role.id === user.roleId)?.name || "Unknown";
      const roleName = baseRoleName === "Auditor" ? user.isPrimaryAuditor ? "Primary Auditor" : "Secondary Auditor" : baseRoleName;
      const isLoggedInUser = user.email === loggedInUserEmail;
      const isSelected = selectedUsers.includes(user.id);
      const userStatus = getUserStatus(user);
      const isActiveUser = userStatus === "ACTIVE";
      const isToggleDisabled = isLoggedInUser;
      return (
        <tr key={user.id} className={!isActiveUser ? "inactive-user-row" : ""}>
          <td> <input type="checkbox" checked={isSelected} onChange={() => toggleUserSelection(user.id)} disabled={isLoggedInUser || !isActiveUser} /></td>
          <td>{user.firstName}</td>
          <td>{user.lastName}</td>
          <td> {user.email} {isLoggedInUser && <span className="you-text">(You)</span>}
          </td>
          <td>{roleName}</td>
          {isAuditorRole && (
            <>
              <td>{getAuditorTypeDescription(user.auditorType)}</td>
              <td>{getSkillDescriptions(user.skills || [])}</td>
            </>
          )}
          {fields.edit && (
            <>
              <td className="actions">
                <button className={`action-button ${isToggleDisabled ? "disabled-button" : ""}`} onClick={() => handleEditUserClick(user)} disabled={isToggleDisabled}> <FaEdit /> </button>
              </td>
              <td className="status-cell">
                <label className={`switch ${isToggleDisabled ? "disabled-toggle" : ""}`}>
                  <input type="checkbox" checked={isActiveUser} onChange={() => handleStatusToggle(user.id)} disabled={isToggleDisabled} />
                  <span className="slider round"></span>
                </label>
                <span className="status-active-inactive">
                  {statusEnum.find((status) => status.value === userStatus)
                    ?.description || userStatus}
                  {!isActiveUser && <span className="inactive-label"></span>}
                </span>
              </td>
            </>
          )}
        </tr>
      );
    });
  };
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      sessionStorage.setItem("userManagementCurrentPage", String(newPage));
    }
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      sessionStorage.setItem("userManagementCurrentPage", String(newPage));
    }
  };
  const handleBackClick = () => {
    setSelectedGroup(null);
    setIsPromptVisible(true);
  };
  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = Number(e.target.value);
    setItemsPerPage(newSize);
    setCurrentPage(1);
    sessionStorage.setItem("userManagementItemsPerPage", String(newSize));
    sessionStorage.setItem("userManagementCurrentPage", "1");
  };
  const handleBack = () => {
    navigate(-1);
  };
  const getPageNumbers = (): number[] => {
    const pages: number[] = [];
    if (totalPages <= 3) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage === 1) {
        pages.push(1, 2, 3);
      } else if (currentPage === totalPages) {
        pages.push(totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(currentPage - 1, currentPage, currentPage + 1);
      }
    }
    return pages;
  };
  return (
    <div className="table-details">
      {isPromptVisible && (
        <div className="prompt-overlay">
          <div className="prompt-box">
            <h2>Select User Group</h2>
            <select onChange={(e) => handleGroupSelection(e.target.value)} defaultValue="" className="group-select">
              <option value="" disabled> Choose a group </option>
              {Array.isArray(roles) && roles.length > 0 ? (
                roles.map((role) => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))
              ) : (
                <option disabled>Loading roles...</option>
              )}
            </select>
            <button className="back-button-user" onClick={handleBack}>Back</button>
          </div>
        </div>
      )}
      {isAddUserPopupVisible && (
        <AddUserPopup roles={roles} selectedGroup={selectedGroup} newUserData={newUserData} handleInputChange={handleInputChange} handleSaveNewUser={handleSaveNewUser} handleClosePopup={handleClosePopup} auditorSkills={auditorSkills} auditorTypes={auditorTypes} />
      )}
      {isEditUserPopupVisible && editUserData && (
        <EditUserPopup roles={roles} editUserData={editUserData} handleEditInputChange={handleEditInputChange} handleSaveEditedUser={handleSaveEditedUser} setIsEditUserPopupVisible={setIsEditUserPopupVisible} auditorSkills={auditorSkills} />
      )}
      {!isPromptVisible && (
        <>
          <div className="table-title">
            <h2> {" "} User Management
              {selectedGroup && (
                <span> {" "} - {roles.find((role) => role.id === selectedGroup)?.name}</span>
              )}
            </h2>
            <div className="table-title-actions">
              {selectedUsers.length > 0 && fields.delete && (
                <button className="delete-selected-button" onClick={handleBulkDelete}>Inactive Selected ({selectedUsers.length}) </button>
              )}
              {selectedGroup && fields.create && (
                <button className="add-button" onClick={handleAddUserClick}> Add {roles.find((role) => role.id === selectedGroup)?.name} </button>
              )}
            </div>
          </div>
          <div className="table-container">
            <div className="table-inner-container">
              <table className="main-table">
                <thead>
                  <tr>
                    <th>
                      <input type="checkbox" checked={isAllSelected} onChange={toggleSelectAll} />
                    </th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    {isAuditorRole && (
                      <>
                        <th>Auditor Type</th>
                        <th>Skills</th>
                      </>
                    )}
                    {fields.edit && (
                      <>
                        <th>Actions</th>
                        <th>User Status</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>{renderTable()}</tbody>
              </table>
            </div>
          </div>
          <div className="pagination">
            <button className="back-btn" onClick={handleBackClick}> Back</button>
            <button className="info">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalElements)} of{" "}
              {totalElements} items
            </button>
            <div className="pagination-controls">
              <button onClick={() => setCurrentPage(1)} className="pagination-btn" disabled={currentPage === 1}>
                <span className="small-pipe">|</span>&#8249;
              </button>
              <button onClick={handlePreviousPage} className="pagination-btn" disabled={currentPage === 1}>&#8249;</button>
              {getPageNumbers().map((page) => (
                <button key={page} onClick={() => { setCurrentPage(page); sessionStorage.setItem("userManagementCurrentPage", String(page)); }} className={`pagination-btn ${currentPage === page ? "active" : ""}`}> {page}</button>
              ))}
              <button onClick={handleNextPage} className="pagination-btn" disabled={currentPage === totalPages}> &#8250;</button>
              <button onClick={() => setCurrentPage(totalPages)} className="pagination-btn" disabled={currentPage === totalPages}> &#8250;<span className="small-pipe">|</span></button>
            </div>
            <div>
              <select id="itemsPerPageSelect" value={itemsPerPage} onChange={handleItemsPerPageChange} className="items-perpage-select">
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </>
      )}
      {statusToggleUserId && (
        <div className="modal-overlay">
          <div className="modal-content">
            <p> Are you sure you want to{" "}
              {getUserStatus(users.find((u) => u.id === statusToggleUserId)!) === "ACTIVE" ? "deactivate" : "activate"}{" "} this user?
            </p>
            <div className="buttons">
              <button className="cancelbutton" onClick={() => setStatusToggleUserId(null)}> Cancel</button>
              <button className="savebutton" onClick={confirmStatusToggle}>Confirm</button>
            </div>
          </div>
        </div>
      )}
      {showBulkDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <p> Are you sure you want to deactivate {selectedUsers.length}{" "} selected users?</p>
            <div className="buttons">
              <button className="cancelbutton" onClick={() => setShowBulkDeleteConfirm(false)}> No </button>
              <button className="savebutton" onClick={confirmBulkDelete}> Yes</button>
            </div>
          </div>
        </div>
      )}
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default UserManagement;